import * as React from 'react';
import InfiniteLoader from 'react-window-infinite-loader';
import strings from 'owa-strings/owa-mini-common-strings';
import useLoadMoreItems, { FetchMoreItems } from '../utils/useLoadMoreItems';
import { addAriaLiveLabel, removeAriaLiveLabel } from 'mini-accessibility';
import { format } from 'owa-strings';
import { MAIL_MODULE_SWITCHER_ICON_CLICKED, mailEventEmitter } from 'mini-mail-event-emitters';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useWindowDimensions, WindowDimensions } from 'mini-hooks/lib/useWindowDimensions';
import { VariableSizeList as List } from 'react-window';

export interface ItemRendererProp<T> {
    index: number;
    data: T[];
    style: React.CSSProperties;
    isScrolling?: boolean;
    forwardedRefs?: {
        switchRef: React.RefObject<HTMLDivElement>;
        filterRef: React.RefObject<HTMLDivElement>;
    };
    forwardedRef?: React.RefObject<HTMLDivElement>;
    showAd?: boolean;
    setShowAdState?: (value: any) => any;
}

export interface VirtualizedListProps<T> {
    itemData: T[];

    isRTL: boolean;

    containerHeight: number | ((windowDimensions: WindowDimensions) => number);

    itemReactKey: (index: number, itemData: T[]) => string;

    itemHeight: (index: number) => number;

    itemRenderer: (props: ItemRendererProp<T>) => JSX.Element | null;

    itemCount: number;

    getIsItemLoaded: (index: number) => boolean;

    fetchMoreItems: FetchMoreItems;

    preFetchThreshold?: number;

    overscanCount?: number;

    // enableIsScrolling is enabled for the list, the component also receives an additional isScrolling boolean prop
    enableIsScrolling?: boolean;

    shouldScrollToTop?: boolean;

    shouldScrollToBottom?: boolean;

    // css classes to be applied to the virtualized list view container
    className?: string;
}

const VirtualizedList = <T extends {}>(props: VirtualizedListProps<T>) => {
    const windowDimensions: WindowDimensions = useWindowDimensions();
    const fetchMoreItems = useLoadMoreItems(props.fetchMoreItems);
    const listRef = useRef(null);
    const { itemCount } = props;
    const containerHeight =
        typeof props.containerHeight === 'function'
            ? props.containerHeight(windowDimensions)
            : props.containerHeight;

    const a11yMessage = useRef(null);
    useEffect(() => {
        if (a11yMessage.current) {
            removeAriaLiveLabel(a11yMessage.current);
        }
        const itemCountDisplay = itemCount - 1;
        a11yMessage.current = itemCountDisplay > 0 ? announcingNumberOfSearchResults() : null;
        if (a11yMessage.current) {
            addAriaLiveLabel(a11yMessage.current, 'assertive');
        }
        return () => {
            if (a11yMessage.current) {
                removeAriaLiveLabel(a11yMessage.current);
            }
        };
    }, [itemCount]);

    useLayoutEffect(() => {
        if (listRef && listRef.current) {
            // Invalidate the itemSizeCache to trigger itemSize computation again.
            // We can optimize it instead of invalidating all the cache we can run
            // computation from specific index.
            listRef.current._listRef.resetAfterIndex(0);
        }
    }, [props.itemData]);

    useLayoutEffect(() => {
        const scrollableList = listRef?.current?._listRef;

        if (scrollableList && props.shouldScrollToBottom) {
            scrollableList.scrollToItem(itemCount - 1);
        }
    }, [props.shouldScrollToBottom]);

    useLayoutEffect(() => {
        if (listRef && listRef.current && props.shouldScrollToTop) {
            listRef.current._listRef.scrollTo(0);
        }
    }, [props.shouldScrollToTop]);

    const announcingNumberOfSearchResults = () => {
        if (itemCount == 0) {
            const message = strings.searchResultsCount;
            return format(message, itemCount);
        }
        const message = strings.searchResultsCount;
        return format(message, itemCount - 1);
    };

    //Effect to register an event listener, when mail icon in module switcher is clicked and list should scroll to top
    useEffect(() => {
        const scrollToTop = () => {
            listRef?.current?._listRef?.scrollTo(0);
        };
        mailEventEmitter.on(MAIL_MODULE_SWITCHER_ICON_CLICKED, scrollToTop);
        return () => {
            mailEventEmitter.off(MAIL_MODULE_SWITCHER_ICON_CLICKED, scrollToTop);
        };
    }, []);

    return (
        <InfiniteLoader
            isItemLoaded={props.getIsItemLoaded}
            itemCount={itemCount}
            ref={listRef}
            loadMoreItems={fetchMoreItems}
            threshold={props.preFetchThreshold}>
            {({ onItemsRendered, ref }) => (
                <List
                    direction={props.isRTL ? 'rtl' : 'ltr'}
                    itemData={props.itemData}
                    height={containerHeight}
                    useIsScrolling={props.enableIsScrolling}
                    itemCount={itemCount}
                    overscanCount={props.overscanCount}
                    itemSize={props.itemHeight}
                    itemKey={props.itemReactKey}
                    ref={ref}
                    onItemsRendered={onItemsRendered}
                    className={props.className}>
                    {props.itemRenderer}
                </List>
            )}
        </InfiniteLoader>
    );
};

VirtualizedList.defaultProps = {
    preFetchThreshold: 15,
    overscanCount: 2,
    isRTL: false,
    enableIsScrolling: false,
    shouldScrollToTop: false,
};

export default VirtualizedList;
